import {TextField} from '@finpay-development/shared-components';
import {Box, MenuItem} from '@mui/material';
import {useDispatch, useSelector} from 'react-redux';
import {PatientEncounter} from '@finpay-development/shared-platform/lib/esm/patient/components/models/patient-encounter';
import {getInstanceOfCare} from '@finpay-development/shared-platform/lib/esm/patient/state/patient-thunk';
import {Utils} from '@finpay-development/shared-platform/lib/esm/shared/utils';
import {RootState} from 'src/shared/state/root-reducer';
import {AppDispatch} from 'src/shared/state/store';

interface EstimatesIocSelectorProps {
    onIocChange?: (encounterId: number) => void | Promise<void>;
}

function formatIocLabel(encounter: PatientEncounter): string {
    const rawDate = encounter?.instanceCreateDt || encounter?.createDt;
    const date = rawDate ? Utils.convertDate(new Date(rawDate)) : '';
    const id = encounter?.patientEncounterId ?? '';
    if (!date) return String(id);
    return `${date} - ${id}`;
}

function getIocCreateTime(encounter: PatientEncounter): number {
    return new Date(
        encounter?.createDt || encounter?.instanceCreateDt || 0
    ).getTime();
}

export function EstimatesIocSelector({onIocChange}: EstimatesIocSelectorProps) {
    const dispatch = useDispatch<AppDispatch>();

    const selectedEncounter = useSelector(
        (state: RootState) => state.patientContext.selectedEncounter
    );
    const patientEncounters = useSelector(
        (state: RootState) =>
            state.patientContext.selectedPatient?.patientEncounters ?? []
    );
    const selectedPatientId = useSelector(
        (state: RootState) => state.patientContext.selectedPatient?.patientId
    );
    const isLoadingIoc = useSelector(
        (state: RootState) => state.patientContext.isLoading?.patientIOC
    );
    const isCreatingIoc = useSelector(
        (state: RootState) => state.patientContext.isLoading?.createPatientIoc
    );
    const isLoadingDemographics = useSelector(
        (state: RootState) =>
            state.patientContext.isLoading?.patientDemographics
    );

    const selectedEncounterId = selectedEncounter?.patientEncounterId;

    // Sort by create time, then by encounter id
    const sortedEncounters = [...patientEncounters].sort((a, b) => {
        const byDate = getIocCreateTime(b) - getIocCreateTime(a);
        if (byDate !== 0) return byDate;
        return (b.patientEncounterId || 0) - (a.patientEncounterId || 0);
    });

    const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const encounterId = Number(event.target.value);
        if (!selectedPatientId || !encounterId || encounterId < 1) {
            return;
        }
        if (encounterId === selectedEncounterId) {
            return;
        }

        await dispatch(
            getInstanceOfCare({
                patientId: selectedPatientId,
                encounterId,
            })
        );

        await onIocChange?.(encounterId);
    };

    if (!selectedPatientId || selectedPatientId < 1) {
        return null;
    }

    return (
        <Box
            sx={{
                minWidth: '220px',
                mb: 2,
                '& .MuiInputBase-root': {
                    py: 0,
                },
                '& .MuiInputBase-input': {
                    py: 1,
                },
            }}
        >
            <TextField
                select
                label="Instance Of Care"
                name="instanceOfCareId"
                value={
                    selectedEncounterId && selectedEncounterId > 0
                        ? selectedEncounterId
                        : ''
                }
                onChange={handleChange}
                onBlur={() => {}}
                disabled={
                    !!isLoadingIoc ||
                    !!isCreatingIoc ||
                    !!isLoadingDemographics ||
                    sortedEncounters.length === 0
                }
                test-id="estimates-ioc-selector"
            >
                {sortedEncounters.length === 0 && (
                    <MenuItem value="" disabled>
                        No Instances Of Care
                    </MenuItem>
                )}
                {sortedEncounters.map((encounter: PatientEncounter) => (
                    <MenuItem
                        key={encounter.patientEncounterId}
                        value={encounter.patientEncounterId}
                    >
                        {formatIocLabel(encounter)}
                    </MenuItem>
                ))}
            </TextField>
        </Box>
    );
}

export default EstimatesIocSelector;
